import axios from "axios";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { getUIConfig } from "../api/ScoreboardApi";
import { RouteConstants } from "../constants/RouteConstants";
import { DEFAULT_STRIKE_ZONE, PITCH_RESET_TIMEOUT } from "../constants/StrikeZoneConstants";
import { MockPitches } from "../data/MockData";
import { DefaultUIConfig, VenueUIConfig, VenueUIConfigOptions } from "../types/Config";
import { CompletePitchData, Pitch, StrikeZone, TabsPitchData } from "../types/StrikeZone";
import { usePitchDataSse } from "../util/pitchDataSse";
import { useSearchParams } from "./SearchParamsContext";

export type DataMode = "sse" | "replay" | "debug" | "config";

type ConnectionContext = {
  mode?: DataMode;
  strikeZone?: StrikeZone;
  pitch?: Pitch;
  uiConfig: VenueUIConfigOptions;
  thinConfig: VenueUIConfigOptions;
  connected: boolean;
  isChallenge: boolean;
};

const ConnectionContext = createContext<ConnectionContext | undefined>(undefined);

const DEBUG_MODE = process.env.DEBUG_MODE === "true";

const ConnectionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const {
    params: { venueGuid, playId, mode: urlMode },
  } = useSearchParams();
  const location = useLocation();

  // Context Variables
  const [strikeZone, setStrikeZone] = useState<StrikeZone>(DEFAULT_STRIKE_ZONE);
  const [pitch, setPitch] = useState<Pitch>();
  const [connected, setConnected] = useState<boolean>(false);
  const [isChallenge, setIsChallenge] = useState<boolean>(false);

  // Mode
  const [mode, setMode] = useState<DataMode>();
  useEffect(() => {
    if (DEBUG_MODE) {
      setMode("debug");
    } else if (!!playId) {
      setMode("replay");
    } else if ((!!venueGuid && venueGuid === "test") || urlMode === "test" || urlMode === "debug") {
      setMode("debug");
    } else if (location.pathname === RouteConstants.CONFIG) {
      setMode("debug");
    } else if (!!venueGuid) {
      setMode("sse");
    } else {
      setMode(undefined);
    }
  }, [venueGuid, playId, urlMode, DEBUG_MODE]);

  useEffect(() => {
    if (mode == "sse") {
      configureSSE();
    }

    if (mode == "replay") {
      configureReplayMode();
    }

    if (mode == "debug") {
      configureDebugMode();
    }
  }, [mode]);

  const [sseRegistered, setRegisteredConnected] = useState<boolean>(false);
  const timerRef = useRef(null);

  /* SSE Configuration */
  const [sseConnected, data] = usePitchDataSse(mode, venueGuid);
  const [config, setConfig] = useState<VenueUIConfig | undefined>();

  const configureSSE = () => {
    console.log("Configuring SSE.");
    setupData().then((_) => console.log("Venue Registered."));
  };

  const setupData = async () => {
    try {
      const result: VenueUIConfig = await getUIConfig(venueGuid);
      if (!!result.strikeZoneConfig) {
        setConfig({
          ...result,
          strikeZoneConfig: JSON.parse(result.strikeZoneConfig as string),
          thinConfig: JSON.parse(result.thinConfig as string),
        });
      } else {
        setConfig({ ...result, strikeZoneConfig: DefaultUIConfig, thinConfig: DefaultUIConfig });
      }
      setRegisteredConnected(true);
    } catch (e) {
      console.error(`Error Registering Venue: ${venueGuid}`, e);
      setRegisteredConnected(false);
    }
  };

  useEffect(() => {
    if (mode === "debug") {
      setConnected(true);
    } else {
      setConnected(sseConnected && sseRegistered && !!config);
    }
  }, [sseConnected, sseRegistered, config, mode]);

  useEffect(() => {
    onMessage(data as CompletePitchData);
  }, [data]);

  /* Replay Mode Configuration */
  const configureReplayMode = () => {
    console.log("Replay Mode.");
    getTabsPitchData();
  };

  const getTabsPitchData = async () => {
    const url = `${process.env.BATRACK_URL}/v1/plays/${playId}`;
    const res: TabsPitchData = await axios.get(url).then((res) => res.data.pitchData);

    const { szTop, szBottom, szWidthInches: szWidth } = res;
    setStrikeZone({ szTop, szBottom, szWidth });

    const { absCall, xcoordinate: xCoord, zcoordinate: zCoord, pitchType: type, pitchSpeed: speed } = res;
    setIsChallenge(false);
    setPitch({
      isChallenge: false,
      pitchHand: "R",
      isStrike: absCall === "strike",
      xCoord,
      zCoord,
      type,
      speed,
      inPlay: false,
    });

    setConnected(true);
  };

  /* Debug Mode Configuration */
  const configureDebugMode = () => {
    console.log("Debug Mode.");
    setConnected(true);
    void setupData();
    setInterval(
      () => {
        const data = MockPitches[Math.floor(Math.random() * MockPitches.length)];
        onMessage(data);
      },
      !!config?.strikeZoneConfig ? (config.strikeZoneConfig as VenueUIConfigOptions).removePitchIn * 1000 : 5000,
    );
  };

  /* Helper Functions */
  const updateStrikeZone = (data: CompletePitchData) => {
    const { szTop, szBottom, szWidth = 17 } = data;
    if (!strikeZone || strikeZone.szTop != szTop || strikeZone.szBottom != szBottom || strikeZone.szWidth != szWidth) {
      setStrikeZone({ szTop, szBottom, szWidth });
    }
  };

  const updatePitch = (data: CompletePitchData) => {
    const { pitchHand, isStrike, xCoord, zCoord, type, speed, inPlay, exitVelocity, launchAngle, isChallenge } = data;
    setIsChallenge(isChallenge);
    setPitch({ pitchHand, isStrike, xCoord, zCoord, type, speed, inPlay, exitVelocity, launchAngle, isChallenge });
  };

  const onMessage = (data?: CompletePitchData) => {
    if (!!data) {
      updateStrikeZone(data);
      updatePitch(data);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => setPitch(undefined), PITCH_RESET_TIMEOUT);
    }
  };

  return (
    <ConnectionContext.Provider
      value={{
        mode,
        strikeZone,
        pitch,
        connected,
        uiConfig: !!config?.strikeZoneConfig ? (config.strikeZoneConfig as VenueUIConfigOptions) : DefaultUIConfig,
        thinConfig: !!config?.thinConfig ? (config.thinConfig as VenueUIConfigOptions) : DefaultUIConfig,
        isChallenge,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
};

const useConnection = (): ConnectionContext => {
  const connectionContext = useContext<ConnectionContext>(ConnectionContext);
  if (connectionContext === undefined) {
    throw new Error(`useConnection must be used within a ConnectionProvider`);
  }
  return connectionContext;
};

export { ConnectionProvider, useConnection };
